import React from "react";
import {
  COORDINATE_FORMATS,
  COORDINATE_LIMITS,
  CoordinatesValue,
  GpsData,
  convertDD2DMS,
  convertDMS2DD,
  useGpsCoordinatesForm,
} from "hooks/useGpsCoordinates";
import useLocationFormatter from "hooks/useLocationFormatter";
import TextInput from "view/pages/MyProfile/Components/Inputs/TextInput";
import { RadioBtn } from "Components/RadioBtn";
import { MaterialUiSelect } from "../Select";

interface GpsCoordinatesFilterInputsProps {
  index: number;
  value: any;
  setFieldValue: (field: string, value: any) => void;
  setFieldTouched: (
    field: string,
    isTouched?: boolean,
    shouldValidate?: boolean,
  ) => void;
  otherQueryFilterErrors: any;
  otherQueryFilterTouched: any;
  handleBlur: (e: any) => void;
}

const GpsCoordinatesFilterInputs: React.FC<GpsCoordinatesFilterInputsProps> = ({
  index,
  value,
  setFieldValue,
  setFieldTouched,
  otherQueryFilterErrors,
  otherQueryFilterTouched,
  handleBlur,
}) => {
  const { formatLocation } = useLocationFormatter();

  const format =
    value?.CoordinatesFormat ||
    value?.coordinatesFormat ||
    value?.coordinates_format ||
    "";
  const isDecimalDegrees = format === COORDINATE_FORMATS.DECIMAL_DEGREES;
  const isDMS = format === COORDINATE_FORMATS.DEGREES_MINUTES_SECONDS;
  const isDMM = format === COORDINATE_FORMATS.DEGREES_DECIMAL_MINUTES;

  const normalizeDecimalValue = (val: any) => {
    if (val === "" || val === null || val === undefined) return undefined;
    const num = typeof val === "number" ? val : Number(val);
    return Number.isFinite(num) ? num : undefined;
  };

  const trimDecimals = (val: number, decimals = 6) => {
    if (!Number.isFinite(val)) return "";
    const factor = Math.pow(10, decimals);
    const rounded = Math.round(val * factor) / factor;
    const fixed = rounded.toFixed(decimals);
    return fixed.replace(/\.0+$/, "").replace(/(\.\d+?)0+$/, "$1");
  };

  const getDecimalCoordinate = (
    coord: CoordinatesValue | number | string,
    requiresSeconds: boolean,
  ) => {
    if (coord && typeof coord === "object") {
      const hasDirection = !!coord.direction;
      const hasDegrees = coord.degrees !== "" && coord.degrees !== undefined;
      const hasMinutes = coord.minutes !== "" && coord.minutes !== undefined;
      const hasSeconds =
        !requiresSeconds ||
        (coord.seconds !== "" && coord.seconds !== undefined);

      if (!hasDirection || !hasDegrees || !hasMinutes || !hasSeconds) {
        return undefined;
      }

      return convertDMS2DD(
        coord.degrees ?? "0",
        coord.direction ?? "N",
        coord.minutes ?? "0",
        coord.seconds ?? "0",
      );
    }

    return normalizeDecimalValue(coord);
  };

  const getDisplayCoordinate = (
    coord: CoordinatesValue | number | string,
    isLongitude: boolean,
  ) => {
    if (isDecimalDegrees) {
      if (coord && typeof coord === "object") {
        const decimal = getDecimalCoordinate(coord, false);
        return decimal ?? Number.NaN;
      }
      const decimal = normalizeDecimalValue(coord);
      return decimal ?? Number.NaN;
    }

    if (coord && typeof coord === "object") {
      return coord;
    }

    const decimal = normalizeDecimalValue(coord);
    if (decimal === undefined) return {};

    const converted = convertDD2DMS(decimal, isLongitude);
    if (isDMM) {
      const minutes = converted.minutes + converted.seconds / 60;
      return {
        direction: converted.direction,
        degrees: String(converted.degrees),
        minutes: trimDecimals(minutes),
      };
    }

    return {
      direction: converted.direction,
      degrees: String(converted.degrees),
      minutes: String(converted.minutes),
      seconds: String(converted.seconds),
    };
  };

  const rawLatitude = value?.Latitude ?? value?.latitude ?? "";
  const rawLongitude = value?.Longitude ?? value?.longitude ?? "";

  const gpsValue: GpsData = {
    CoordinatesFormat: format,
    Latitude: getDisplayCoordinate(rawLatitude, false),
    Longitude: getDisplayCoordinate(rawLongitude, true),
  };

  const toFormValue = (data: GpsData) => {
    const isDecimal =
      data.CoordinatesFormat === COORDINATE_FORMATS.DECIMAL_DEGREES;
    const isDMM =
      data.CoordinatesFormat === COORDINATE_FORMATS.DEGREES_DECIMAL_MINUTES;

    const toDmsValue = (coord: any, isLongitude: boolean) => {
      if (coord && typeof coord === "object") return coord;
      const decimal = normalizeDecimalValue(coord);
      if (decimal === undefined) return {};
      const converted = convertDD2DMS(decimal, isLongitude);
      if (isDMM) {
        const minutes = converted.minutes + converted.seconds / 60;
        return {
          direction: converted.direction,
          degrees: String(converted.degrees),
          minutes: trimDecimals(minutes),
        };
      }
      return {
        direction: converted.direction,
        degrees: String(converted.degrees),
        minutes: String(converted.minutes),
        seconds: String(converted.seconds),
      };
    };

    const toDecimalValue = (coord: any) => {
      if (coord && typeof coord === "object") {
        const decimal = getDecimalCoordinate(coord, false);
        return decimal ?? "";
      }
      if (coord === "" || coord === null || coord === undefined) return "";
      return coord;
    };

    const nextLatitude = isDecimal
      ? toDecimalValue(data.Latitude)
      : toDmsValue(data.Latitude, false);
    const nextLongitude = isDecimal
      ? toDecimalValue(data.Longitude)
      : toDmsValue(data.Longitude, true);

    return {
      coordinates_format: data.CoordinatesFormat,
      latitude: nextLatitude,
      longitude: nextLongitude,
    };
  };

  const {
    handleFormatChange,
    handleCoordinateChange,
    handleDMSChange,
    formatChoices,
  } = useGpsCoordinatesForm({
    value: gpsValue,
    onChange: (data: GpsData) =>
      setFieldValue(`otherQueryFilter[${index}].value`, toFormValue(data)),
    disabled: false,
    elements: [
      {
        name: "CoordinatesFormat",
        choices: Object.values(COORDINATE_FORMATS).map((v) => ({
          text: v,
          value: v,
        })),
      },
    ],
    formatLocation,
  });

  // Helper to get field errors and touched state
  const getFieldState = (field: string) => {
    const lowerField = field.toLowerCase();
    const touched =
      otherQueryFilterTouched?.value?.[field] ??
      otherQueryFilterTouched?.value?.[lowerField];
    const error =
      otherQueryFilterErrors?.value?.[field] ??
      otherQueryFilterErrors?.value?.[lowerField];
    const normalizedError = typeof error === "string" ? error : undefined;

    return { error: touched ? normalizedError : undefined, touched };
  };

  const rawFormatError =
    otherQueryFilterErrors?.value?.CoordinatesFormat ??
    otherQueryFilterErrors?.value?.coordinatesFormat ??
    otherQueryFilterErrors?.value?.coordinates_format;
  const formatError =
    typeof rawFormatError === "string" ? rawFormatError : undefined;
  const formatTouched =
    otherQueryFilterTouched?.value?.CoordinatesFormat ??
    otherQueryFilterTouched?.value?.coordinatesFormat ??
    otherQueryFilterTouched?.value?.coordinates_format;
  const hasCoordinateTouched =
    otherQueryFilterTouched?.value?.Latitude ||
    otherQueryFilterTouched?.value?.Longitude ||
    otherQueryFilterTouched?.value?.latitude ||
    otherQueryFilterTouched?.value?.longitude;
  const showFormatError =
    !!formatError && (formatTouched || hasCoordinateTouched);

  // Decimal degrees input
  const renderDecimalInput = (type: "Latitude" | "Longitude") => {
    const isLat = type === "Latitude";
    const limit = isLat
      ? COORDINATE_LIMITS.LATITUDE
      : COORDINATE_LIMITS.LONGITUDE;
    const { error, touched } = getFieldState(type);
    const rawValue = isLat ? rawLatitude : rawLongitude;
    const inputValue =
      typeof rawValue === "number" && !Number.isFinite(rawValue)
        ? ""
        : typeof rawValue === "number"
          ? trimDecimals(rawValue)
          : typeof rawValue === "object"
            ? (() => {
                const decimal = getDecimalCoordinate(
                  rawValue as CoordinatesValue,
                  false,
                );
                return decimal === undefined ? "" : trimDecimals(decimal);
              })()
            : rawValue ?? "";

    return (
      <div className="w-full">
        <TextInput
          label={type}
          type="number"
          name={`otherQueryFilter[${index}].value.${type}`}
          setFieldValue={setFieldValue}
          value={inputValue as number | string}
          error={error}
          touched={touched}
          handleChange={(e: any) => {
            const val = e.target.value;
            if (val === "") return handleCoordinateChange(type, "");
            const num = parseFloat(val);
            if (!isNaN(num) && (num < limit.min || num > limit.max)) return;
            handleCoordinateChange(type, val);
          }}
          handleBlur={handleBlur}
          inputClassName=" w-full"
          placeholder={`Enter ${type} (${isLat ? "-90 to 90" : "-180 to 180"})`}
        />
      </div>
    );
  };

  // DMS/DMM input fields
  const renderDmsInput = (type: "Latitude" | "Longitude") => {
    const isLat = type === "Latitude";
    const coordValue = (gpsValue[type] as CoordinatesValue) || {};
    const coordTouched =
      otherQueryFilterTouched?.value?.[type] ||
      otherQueryFilterTouched?.value?.[type.toLowerCase()] ||
      {};
    const hasAnyTouched =
      coordTouched?.direction ||
      coordTouched?.degrees ||
      coordTouched?.minutes ||
      coordTouched?.seconds;
    const directionOptions = (isLat ? ["N", "S"] : ["E", "W"]).map((d) => ({
      label: d,
      value: d,
    }));

    return (
      <div className="w-full bg-white rounded-lg p-4 shadow-sm">
        {/* Header */}
        <h4 className="text-sm font-semibold text-slate-700">
          {type} <span className="text-red-500">*</span>
        </h4>

        {/* Input Fields Grid */}
        <div className="mt-3 flex flex-wrap items-start gap-3">
          {/* Direction */}
          <div className="flex flex-col w-full sm:w-24 shrink-0 py-2">
            <label className="pb-1 text-sm font-medium text-slate-600">
              Direction <span className="text-red-500">*</span>
            </label>
            <MaterialUiSelect
              options={directionOptions}
              labelKey="label"
              valueKey="value"
              initialValue={coordValue?.direction || ""}
              error={
                hasAnyTouched && !coordValue?.direction ? "Required" : null
              }
              handleChange={(opt: any) => {
                handleDMSChange(type, "direction", opt?.value ?? "");
                setFieldTouched(
                  `otherQueryFilter[${index}].value.${type}.direction`,
                  true,
                  false,
                );
                setFieldTouched(
                  `otherQueryFilter[${index}].value.${type.toLowerCase()}.direction`,
                  true,
                  false,
                );
              }}
              className="capitalize h-[42px]"
              height={42}
              width="100%"
            />
          </div>

          {/* Degrees */}
          <div className="flex flex-col  flex-1">
            <TextInput
              label="Degrees"
              type="number"
              name={`otherQueryFilter[${index}].value.${type}.degrees`}
              setFieldValue={setFieldValue}
              value={coordValue?.degrees ?? ""}
              handleChange={(e: any) =>
                handleDMSChange(type, "degrees", e.target.value)
              }
              handleBlur={handleBlur}
              inputClassName=" w-full h-[42px] border-slate-300"
              placeholder={isLat ? "0–90" : "0–180"}
              error={
                hasAnyTouched && !coordValue?.degrees ? "Required" : undefined
              }
              touched={coordTouched?.degrees}
            />
          </div>

          {/* Minutes */}
          <div className="flex flex-col  flex-1">
            <TextInput
              label="Minutes"
              type="number"
              name={`otherQueryFilter[${index}].value.${type}.minutes`}
              setFieldValue={setFieldValue}
              value={coordValue?.minutes ?? ""}
              handleChange={(e: any) =>
                handleDMSChange(type, "minutes", e.target.value)
              }
              handleBlur={handleBlur}
              inputClassName=" w-full h-[42px] border-slate-300"
              placeholder={isDMM ? "0.00–59.99" : "0–59"}
              error={
                hasAnyTouched && !coordValue?.minutes ? "Required" : undefined
              }
              touched={coordTouched?.minutes}
            />
          </div>

          {/* Seconds (DMS only) */}
          {isDMS && (
            <div className="flex flex-col  flex-1">
              <TextInput
                label="Seconds"
                type="number"
                name={`otherQueryFilter[${index}].value.${type}.seconds`}
                setFieldValue={setFieldValue}
                value={coordValue?.seconds ?? ""}
                handleChange={(e: any) =>
                  handleDMSChange(type, "seconds", e.target.value)
                }
                handleBlur={handleBlur}
                inputClassName=" w-full h-[42px] border-slate-300"
                placeholder="0.00–59.99"
                error={
                  hasAnyTouched && !coordValue?.seconds ? "Required" : undefined
                }
                touched={coordTouched?.seconds}
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="w-full bg-slate-50 rounded-xl  overflow-hidden">
      {/* Format Selection Section */}
      <div className="bg-white">
        <h3 className="text-sm font-semibold text-slate-800">
          Coordinates Format
        </h3>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {formatChoices.map((f: any) => (
            <RadioBtn
              value={f.value}
              checked={format === f.value}
              handleChange={() => {
                handleFormatChange({ value: f.value });
                setFieldTouched(
                  `otherQueryFilter[${index}].value.CoordinatesFormat`,
                  true,
                  false,
                );
                setFieldTouched(
                  `otherQueryFilter[${index}].value.coordinatesFormat`,
                  true,
                  false,
                );
                setFieldTouched(
                  `otherQueryFilter[${index}].value.coordinates_format`,
                  true,
                  false,
                );
              }}
              className="w-full"
              labelContent={
                <div className="flex items-center gap-2 w-full">
                  <span
                    className={`
                      text-sm font-medium transition-colors
                      ${format === f.value ? "text-blue-700" : "text-slate-700"}
                    `}
                  >
                    {f.text}
                  </span>
                </div>
              }
            />
          ))}
        </div>

        {showFormatError && (
          <div className="mt-3 p-2 bg-red-50 border border-red-200 ">
            <p className="text-xs text-red-600">{formatError}</p>
          </div>
        )}
      </div>

      {/* Coordinate Inputs Section */}

      {isDecimalDegrees ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {renderDecimalInput("Latitude")}
          {renderDecimalInput("Longitude")}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {renderDmsInput("Latitude")}
          {renderDmsInput("Longitude")}
        </div>
      )}
    </div>
  );
};

export default GpsCoordinatesFilterInputs;
